import { TrackPublication } from 'livekit-client';

import { DeviceOption } from './types';

// Chrome lists the system default device a second time under these ids, so
// one microphone would otherwise show up as two choices.
const ALIAS_IDS = ['default', 'communications'];

const isAlias = (device: MediaDeviceInfo) =>
  ALIAS_IDS.includes(device.deviceId);

export const deviceOptions = (devices: MediaDeviceInfo[]): DeviceOption[] =>
  devices
    .filter((device) => device.deviceId !== '' && !isAlias(device))
    .map(({ deviceId, label }) => ({ deviceId, label }));

export const trackDeviceId = (
  publication?: TrackPublication
): string | undefined =>
  publication?.track?.mediaStreamTrack.getSettings().deviceId;

// A pick made while the microphone is off takes effect when it is turned back
// on, so until then the running track still has the old device's label.
export const activeDeviceLabel = ({
  devices,
  activeDeviceId,
  trackLabel,
}: {
  devices: MediaDeviceInfo[];
  activeDeviceId?: string;
  trackLabel?: string;
}): string | undefined =>
  devices.find((device) => device.deviceId === activeDeviceId)?.label ||
  trackLabel;

// LiveKit keeps the device the clinician picked, also while the camera or
// microphone is off. Until a pick, it reports "default", and the running track
// says which device that is. Without a track, Chrome's alias entry shares its
// groupId with the real device it points at.
export const resolveActiveDeviceId = ({
  devices,
  activeDeviceId,
  trackDeviceId: activeTrackDeviceId,
}: {
  devices: MediaDeviceInfo[];
  activeDeviceId: string;
  trackDeviceId?: string;
}): string | undefined => {
  if (!ALIAS_IDS.includes(activeDeviceId)) return activeDeviceId;
  if (activeTrackDeviceId) return activeTrackDeviceId;
  const alias = devices.find((device) => device.deviceId === activeDeviceId);
  if (!alias) return undefined;
  return devices.find(
    (device) => !isAlias(device) && device.groupId === alias.groupId
  )?.deviceId;
};
